import { api } from '@/lib/api';
import { appSchool, isNativeApp } from '@/lib/native';

/**
 * Fingerprint / face sign-in for the phone app.
 *
 * Turning it on: the phone makes a key pair; the private key goes into Android's secure storage (Keystore-encrypted,
 * opened only after a fingerprint), the public key goes to the server with the person's PIN or password.
 * Signing in: fingerprint → the server sends a one-time code → the phone signs it with the private key → signed in.
 * Money actions still ask for the PIN; the fingerprint only replaces typing the password.
 */
const SERVER = `ng.com.ojpschools.biometric.${appSchool?.code ?? 'app'}`;
const FLAG = 'ojp-biometric';

interface Enrolled { uuid: string; name: string; role?: string }

const plugin = async () => (await import('@capgo/capacitor-native-biometric')).NativeBiometric;
const deviceUuid = (): string => {
  let uuid = localStorage.getItem('ojp-device');
  if (!uuid) { uuid = crypto.randomUUID(); localStorage.setItem('ojp-device', uuid); }
  return uuid;
};
const b64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const pem = (der: ArrayBuffer) => `-----BEGIN PUBLIC KEY-----\n${(b64(der).match(/.{1,64}/g) ?? []).join('\n')}\n-----END PUBLIC KEY-----\n`;
const ALG = { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' } as const;

/** Who fingerprint sign-in is set up for on this phone (null when it is not). */
export function enrolledHere(): Enrolled | null {
  if (!isNativeApp) return null;
  try { return JSON.parse(localStorage.getItem(FLAG) ?? 'null'); } catch { return null; }
}

const within = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([p, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);

/** Does this phone have a fingerprint/face set up? (never waits more than a few seconds) */
export async function biometricAvailable(): Promise<boolean> {
  if (!isNativeApp || !window.crypto?.subtle) return false;
  try {
    const bio = await plugin();
    const r = await within(bio.isAvailable({ useFallback: false }), 4000, null as { isAvailable: boolean } | null);
    // no answer in time: let the fingerprint prompt itself decide
    return r ? !!r.isAvailable : true;
  } catch {
    return false;
  }
}

/** A message a person can act on, from whatever went wrong. */
export function biometricError(e: any): string | null {
  const server = e?.response?.data?.message;
  if (server) return server;
  const m = String(e?.message ?? e ?? '');
  if (/cancel|user.?cancel|negative|10\b|13\b/i.test(m + ' ' + (e?.code ?? ''))) return null; // they pressed Cancel
  if (/not.?enrolled|none.?enrolled|no.?biometric|11\b/i.test(m + ' ' + (e?.code ?? ''))) return 'No fingerprint is saved on this phone. Add one in the phone settings (Security → Biometrics), then try again.';
  if (/lock/i.test(m)) return 'Too many tries. Unlock your phone with its PIN, then try again.';
  return m ? `Fingerprint could not be used: ${m}` : 'Fingerprint could not be used on this phone.';
}

/** Turn it on for the signed-in person. Give the password (right after signing in) or the PIN. */
export async function enrolBiometric(user: { name: string; role?: string }, proof: { password?: string; pin?: string }): Promise<void> {
  const bio = await plugin();
  await bio.verifyIdentity({ reason: 'Confirm it is you', title: 'Turn on fingerprint sign-in', negativeButtonText: 'Cancel' });
  const keys = await crypto.subtle.generateKey({ ...ALG, modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]) }, true, ['sign', 'verify']);
  const publicKey = pem(await crypto.subtle.exportKey('spki', keys.publicKey));
  const privateKey = b64(await crypto.subtle.exportKey('pkcs8', keys.privateKey));
  const uuid = deviceUuid();
  await api.post('/auth/biometric/enrol', { device_uuid: uuid, public_key: publicKey, ...proof });
  await bio.setCredentials({ server: SERVER, username: uuid, password: privateKey });
  localStorage.setItem(FLAG, JSON.stringify({ uuid, name: user.name, role: user.role } satisfies Enrolled));
}

/** Sign in with the fingerprint. Returns the same session payload as a password sign-in. */
export async function signInWithBiometric(): Promise<any> {
  const who = enrolledHere();
  if (!who) throw new Error('Fingerprint sign-in is not set up on this phone.');
  const bio = await plugin();
  await bio.verifyIdentity({ reason: `Sign in as ${who.name}`, title: appSchool?.name ?? 'Sign in', negativeButtonText: 'Use password' });
  const cred = await bio.getCredentials({ server: SERVER });
  const key = await crypto.subtle.importKey('pkcs8', unb64(cred.password), ALG, false, ['sign']);
  const { data: ch } = await api.post('/auth/biometric/challenge', { device_uuid: who.uuid });
  const nonce: string = ch?.data?.nonce;
  const signature = b64(await crypto.subtle.sign(ALG.name, key, new TextEncoder().encode(nonce)));
  const { data } = await api.post('/auth/biometric/login', { device_uuid: who.uuid, signature });
  return data.data;
}

/** Turn it off on this phone (and on the server when signed in). */
export async function disableBiometric(signedIn = true): Promise<void> {
  const who = enrolledHere();
  localStorage.removeItem(FLAG);
  try { await (await plugin()).deleteCredentials({ server: SERVER }); } catch { /* nothing stored */ }
  if (signedIn && who) await api.post('/auth/biometric/disable', { device_uuid: who.uuid }).catch(() => undefined);
}
